type ChatEventHandler = (event: { type: string; data: any }) => void;

interface ChatEventSubscription {
  close: () => void;
}

export function subscribeToChatEvents(
  apiBase: string | undefined,
  token: string | null,
  onEvent: ChatEventHandler
): ChatEventSubscription {
  const controller = new AbortController();
  const base = apiBase?.replace(/\/$/, '');

  if (!base || !token) {
    return { close: () => controller.abort() };
  }

  async function connect() {
    try {
      const res = await fetch(`${base}/api/chat/events`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });

      if (!res.ok || !res.body) return;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!controller.signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';

        for (const chunk of chunks) {
          const typeLine = chunk.split('\n').find(line => line.startsWith('event: '));
          const dataLine = chunk.split('\n').find(line => line.startsWith('data: '));
          if (!typeLine || !dataLine) continue;

          try {
            onEvent({
              type: typeLine.slice(7).trim(),
              data: JSON.parse(dataLine.slice(6)),
            });
          } catch {
            // Ignore malformed events and keep the stream alive.
          }
        }
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error('Chat event stream failed', err);
      }
    }
  }

  connect();

  return {
    close: () => controller.abort(),
  };
}
